import React, { useState, useMemo } from 'react';
import { startsWith } from 'lodash';
import {
  Box,
  Button,
  CardActions,
  CardContent,
  Collapse,
  CollapseIcon,
  Divider,
  ExpandIcon,
  Grid,
  List,
  ListItem,
  Paper,
  Typography,
} from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';

import { AvailityHeaderLogo, Footer } from '@/components';
import { useAppContext } from '@/context';
import { ExistingOrgStep1Img, ExistingOrgStep2Img, ExistingOrgStep3Img } from '@/static';

// handle getting the av portal urls
const { host = '' } = window.location;
const lowercaseHost = host?.toLowerCase();
// check if the page is loaded with '.net' and set the proper domain suffix
const isDotNet = lowercaseHost.indexOf('.net') >= 0;
const domainSuffix = isDotNet ? '.availity.net' : '.availity.com';

const currentEnv = getSpecificEnv();
const basePortalUrl =
  currentEnv?.toLowerCase() === 'prod' ? `https://apps${domainSuffix}` : `https://${currentEnv}-apps${domainSuffix}`;
const avHomePageUrl = `${basePortalUrl}/public/apps/home/#!/`;

const NextStepsExistingOrganization = () => {
  const { queryParams = {}, setCurrentNextStepsPage } = useAppContext();
  const { goto: contextGoTo } = queryParams;

  const [isCollapseOpen, setIsCollapseOpen] = useState(false);

  const gotoValueToUse = useMemo(() => {
    let output;
    if (contextGoTo) {
      // for safety, need to check that the goto url domain matches the window domain
      const { origin = '' } = window.top.location;
      output = startsWith(contextGoTo, origin) ? contextGoTo : basePortalUrl;
    } else {
      output = avHomePageUrl;
    }

    return output;
  }, [contextGoTo]);

  // ensure the correct location is used
  const handlePortalNavigation = () => {
    if (gotoValueToUse.indexOf('public/apps/home') >= 0) {
      window.top.location.href = gotoValueToUse;
    } else {
      window.location.href = gotoValueToUse;
    }
  };

  return (
    <Grid
      id="next-steps-existing-org-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        size={{ xs: 12, sm: 12, md: 6 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        sx={{ minHeight: '100vh' }}
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '700px', lg: '1000px' } }}
          >
            <CardContent>
              <Typography variant="h1" mb={2}>
                Next Steps
              </Typography>
              <Typography variant="h2" mb={2}>
                Join an existing organization
              </Typography>
              <Divider my={2} />
              <Typography variant="h3" my={2}>
                My Office Already Uses Availity Essentials
              </Typography>
              <Typography variant="subtitle1" mb={2}>
                Before you can begin using Availity Essentials, your administrator must add your user ID to the existing
                organization.
              </Typography>
              <Typography variant="h3" mb={2}>
                Who is my Availity Essentials Administrator?
              </Typography>
              <Typography variant="subtitle1" mb={3}>
                Every office has at least one Availity Essentials Administrator. If you are unsure who your
                administrator is, any co-worker currently using Availity can look up your administrator(s).
              </Typography>
              <Box mb={2}>
                <Button
                  color="tertiary"
                  size="small"
                  onClick={() => setIsCollapseOpen(!isCollapseOpen)}
                  startIcon={isCollapseOpen ? <CollapseIcon /> : <ExpandIcon />}
                >
                  Where is administrator information located?
                </Button>
              </Box>
              <Collapse in={isCollapseOpen}>
                <Paper variant="outlined">
                  <CardContent>
                    <List
                      component="ol"
                      sx={{
                        listStyleType: 'decimal',
                        listStylePosition: 'outside',
                        paddingX: 2,
                        marginBottom: 2,
                      }}
                    >
                      <ListItem divider={false} sx={{ display: 'list-item' }}>
                        <Typography variant="subtitle1" mb={2}>
                          Have a co-worker log into Availity Essentials, go to the account section in the upper right
                          corner and select{' '}
                          <Box component="span" sx={{ fontWeight: 'bold' }}>
                            My Account
                          </Box>
                          .
                        </Typography>
                        <Box>
                          <img
                            src={ExistingOrgStep1Img}
                            alt="Attach to an Existing Org Step 1"
                            style={{ height: 200 }}
                          />
                        </Box>
                      </ListItem>
                      <ListItem divider={false} sx={{ display: 'list-item' }}>
                        <Typography variant="subtitle1" mb={2}>
                          Have them select{' '}
                          <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Organization(s)
                          </Box>{' '}
                          in the left panel.
                        </Typography>
                        <Box>
                          <img
                            src={ExistingOrgStep2Img}
                            alt="Attach to an Existing Org Step 2"
                            style={{ height: 200 }}
                          />
                        </Box>
                      </ListItem>
                      <ListItem divider={false} sx={{ display: 'list-item' }}>
                        <Typography variant="subtitle1" mb={2}>
                          Click{' '}
                          <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Open My Administrators
                          </Box>{' '}
                          to view the name and email address of your office&apos;s Availity Essentials administrator(s).
                        </Typography>
                        <Box>
                          <img
                            src={ExistingOrgStep3Img}
                            alt="Attach to an Existing Org Step 3"
                            style={{ height: 200 }}
                          />
                        </Box>
                      </ListItem>
                    </List>
                    <Typography variant="subtitle1">
                      Once you&apos;ve identified your administrator, give them your user ID and let them know you need
                      to be added to the organization. Once you&apos;ve been added, you can begin working in Availity
                      Essentials.
                    </Typography>
                  </CardContent>
                </Paper>
              </Collapse>
            </CardContent>
            <CardActions sx={{ justifyContent: 'end' }}>
              <Button color="secondary" onClick={() => setCurrentNextStepsPage('initial')}>
                Back
              </Button>
              <Button color="primary" onClick={handlePortalNavigation}>
                Continue
              </Button>
            </CardActions>
          </Paper>
        </Grid>
        <Footer />
      </Grid>
    </Grid>
  );
};

export default NextStepsExistingOrganization;
